import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { type ComponentProps, useMemo, useState } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import PageDataState from '@/components/PageDataState'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import ResizeHandle from '@/components/ResizeHandle'
import { FuturesColumnsMenu } from '@/pages/futures/FuturesColumnsMenu'
import { useLocalDatasheetColumns } from '@/utils/datasheetColumns/useLocalDatasheetColumns'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { HIDE_ON_MOBILE_INLINE, TABLE_PAGINATION_COMPACT, tableInnerHeight } from '@/theme/responsive'
import { titleCaseName } from '@/utils/text'

import {
  filterProcurementReferenceRows,
  formatProcurementReferenceValue,
  type ProcurementReferencePageConfig,
} from './procurementReferenceConfig'
import { getTableSortSuffix, sortTableRows, type TableSortState, toggleTableSort } from '../../utils/tableSort'

type MuiTableProps = ComponentProps<typeof Table>
type MuiTableHeadProps = ComponentProps<typeof TableHead>
type MuiTableBodyProps = ComponentProps<typeof TableBody>
type MuiTableRowProps = ComponentProps<typeof TableRow>

interface ProcurementReferenceGridProps {
  page: ProcurementReferencePageConfig
  rows: Record<string, unknown>[]
  search: string
  onSearchChange: (value: string) => void
  loading?: boolean
  error?: unknown
  onRetry?: () => void
  /** Bảng FILL hết chiều cao parent (bỏ cap 620) — chỉ bật khi parent bounded. */
  fillHeight?: boolean
}

export default function ProcurementReferenceGrid({
  page,
  rows,
  search,
  onSearchChange,
  loading = false,
  error,
  onRetry,
  fillHeight = false,
}: ProcurementReferenceGridProps) {
  const [pageIndex, setPageIndex] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filterPopover = useFilterPopover<'search'>()
  const closeFilterPopover = () => filterPopover.close()

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{ key: string; position: ColumnFilterAnchorPosition } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const { getWidth, startResize } = useColumnWidths('procurement.references')

  const columns = page.columns
  const colByField = useMemo(() => new Map(columns.map((c) => [c.field, c])), [columns])

  // Column visibility + reorder menu (§10) — persist per reference page.
  const menuColumns = useMemo(
    () => columns.map((c) => ({ ...c, key: c.field, label: c.headerName })),
    [columns],
  )
  const columnsCtrl = useLocalDatasheetColumns({
    allColumns: menuColumns,
    storageKey: `procurement.references.${page.pageKey}`,
  })
  const visibleColumns = columnsCtrl.visibleColumns

  const searchedRows = useMemo(
    () =>
      filterProcurementReferenceRows(rows, search, columns).map(
        (row, index) => ({ id: `${page.pageKey}-${index}`, ...row }) as Record<string, unknown>,
      ),
    [page.pageKey, columns, rows, search],
  )

  const filteredRows = useMemo(
    () =>
      columnFilters.hasAny
        ? searchedRows.filter((row) => rowMatchesAllColumnFilters(row, columnFilters.filters))
        : searchedRows,
    [searchedRows, columnFilters.filters, columnFilters.hasAny],
  )

  const popoverColumn = useMemo(
    () => (colPopover ? colByField.get(colPopover.key) ?? null : null),
    [colPopover, colByField],
  )
  const popoverKind = useMemo<ColumnFilterKind>(() => {
    if (!popoverColumn) return 'text'
    const colDef: { key: string; kind?: string } = { key: popoverColumn.field, kind: popoverColumn.kind }
    return inferColumnFilterKind(colDef as never, rows as never)
  }, [popoverColumn, rows])
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(rows, colPopover.key) : undefined),
    [popoverKind, colPopover, rows],
  )

  const sortedRows = useMemo(
    () =>
      sortTableRows(
        filteredRows,
        sortState,
        columns.map((c) => ({ field: c.field, kind: c.kind })),
      ),
    [columns, filteredRows, sortState],
  )

  const effectivePageIndex = useMemo(() => {
    if (rowsPerPage === -1) return 0
    const maxPage = Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1)
    return Math.min(pageIndex, maxPage)
  }, [pageIndex, rowsPerPage, sortedRows.length])

  const visibleRows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = effectivePageIndex * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [effectivePageIndex, rowsPerPage, sortedRows])

  const isEmpty = !loading && !error && filteredRows.length === 0
  const minTableWidth = useMemo(
    () => Math.max(1200, visibleColumns.reduce((total, c) => total + (c.width ?? c.minWidth ?? 160), 0)),
    [visibleColumns],
  )

  // Footer Total row — sum only Access `AggregateType=0` columns (convert-table §10.9),
  // computed over all filtered rows (not just the current page).
  const sumFields = useMemo(() => new Set(columns.filter((c) => c.sum).map((c) => c.field)), [columns])
  const hasTotalsRow = sumFields.size > 0
  const footerTotals = useMemo(() => {
    if (!hasTotalsRow) return {} as Record<string, number>
    const totals: Record<string, number> = {}
    for (const key of sumFields) {
      totals[key] = filteredRows.reduce((acc, row) => {
        const v = Number(row[key])
        return acc + (Number.isFinite(v) ? v : 0)
      }, 0)
    }
    return totals
  }, [filteredRows, sumFields, hasTotalsRow])
  const gridKey = `${page.pageKey}:${rowsPerPage}:${effectivePageIndex}:${filteredRows.length}:${visibleColumns.map((c) => c.field).join(',')}`
  const hasActiveFilters = !!search.trim() || columnFilters.hasAny
  const activeFilterCount = (search.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    onSearchChange('')
    columnFilters.clearAll()
    setRowsPerPage(-1)
    setPageIndex(0)
    closeFilterPopover()
  }
  const toggleSort = (field: string) => {
    setSortState((current) => toggleTableSort(current, field))
    setPageIndex(0)
  }

  const searchBtn = (
    <FilterButton label="Search" active={!!search.trim()} onClick={(e) => filterPopover.open('search', e)} />
  )
  const columnsBtn = (
    <Button
      size="small"
      variant="outlined"
      endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
      onClick={(e) => columnsCtrl.openMenu(e.currentTarget)}
      sx={filterBarBtnSx}
    >
      Columns
    </Button>
  )

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        minHeight: 0,
        flex: 1,
        ...(fillHeight ? { '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' } } : {}),
      }}
    >
      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', sm: 'space-between' },
            gap: { xs: 0.5, sm: 1 },
            flexWrap: 'wrap',
            minHeight: { xs: 24, sm: 30 },
            py: 0.125,
          }}
        >
          <FilterBar
            sx={{ px: { xs: 0.25, sm: 0.5 }, pt: 0.75 }}
            hasActiveFilters={hasActiveFilters}
            onClearAll={clearAllFilters}
            activeFilterCount={activeFilterCount}
          >
            {searchBtn}
            {columnsBtn}
          </FilterBar>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {loading && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Loaded: {rows.length}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={effectivePageIndex}
              onPageChange={(_, newPage) => setPageIndex(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPageIndex(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>
        <FuturesColumnsMenu controller={columnsCtrl} />

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={360}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={search}
              onChange={(v) => {
                onSearchChange(v)
                setPageIndex(0)
              }}
              placeholder="Filter loaded rows..."
              label="Search page"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.headerName ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPageIndex(0)
          }}
          options={popoverOptions}
        />

        <PageDataState
          loading={loading}
          error={error}
          empty={isEmpty}
          emptyMessage="No rows (or empty table)."
          onRetry={onRetry}
          minHeight={300}
        >
          <Box sx={fillHeight ? { flex: 1, minHeight: { xs: 280, sm: 320 }, overflow: 'hidden' } : tableInnerHeight(620)}>
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={visibleRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((column) => {
                    const w = getWidth(column.field)
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(column.field)}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'text', position: 'relative' }}
                      >
                        {column.headerName}
                        {getTableSortSuffix(sortState, column.field)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(column.field)}
                          onClick={(position) => setColPopover({ key: column.field, position })}
                          ariaLabel={`Filter ${column.headerName}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(column.field, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: Record<string, unknown>) =>
                visibleColumns.map((column) => {
                  const value = column.proper
                    ? titleCaseName(row[column.field])
                    : formatProcurementReferenceValue(row[column.field], column.kind, column.precision ?? 0)
                  const w = getWidth(column.field)
                  // convert-table §7.8: wrap khi cột flag `wrap` HOẶC user đã resize
                  // → text dài xuống dòng, TableVirtuoso đo lại → dòng auto-cao.
                  const wrap = column.wrap || w != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                        style={{ width: w, minWidth: w, maxWidth: w }}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {value || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={column.field}
                      align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                      sx={{
                        whiteSpace: 'nowrap',
                        py: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        verticalAlign: 'top',
                      }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={value || '-'}
                        sx={{ display: 'inline-block', width: '100%' }}
                      >
                        {value || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              fixedFooterContent={
                hasTotalsRow
                  ? () => (
                      <TableRow>
                        {visibleColumns.map((column, index) => {
                          const w = getWidth(column.field)
                          const isSum = sumFields.has(column.field)
                          return (
                            <TableCell
                              key={column.field}
                              align={column.align ?? (column.kind === 'number' ? 'right' : 'left')}
                              style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                              sx={{
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                py: 0.5,
                                bgcolor: 'background.paper',
                                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}
                            >
                              {isSum
                                ? formatProcurementReferenceValue(
                                    footerTotals[column.field],
                                    'number',
                                    column.precision ?? 0,
                                  )
                                : index === 0
                                  ? 'Total'
                                  : ''}
                            </TableCell>
                          )
                        })}
                      </TableRow>
                    )
                  : undefined
              }
              components={{
                Table: (props: MuiTableProps) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: (props: MuiTableHeadProps) => <TableHead {...props} component="thead" />,
                TableBody: (props: MuiTableBodyProps) => <TableBody {...props} component="tbody" />,
                TableRow: (props: MuiTableRowProps) => <TableRow {...props} hover />,
              }}
            />
          </Box>
        </PageDataState>
      </Box>
    </Box>
  )
}
